import { APP_TO_WEB, msgData, requestApp, resolveNativeCallback, WEB_TO_APP, isNativeApp } from "common/webview";
import { isJustLoggedOut, decodeJwtPayload, isTokenExpired } from "common/utils/auth";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { deviceAtom } from "store/deviceStore";
import { insetAtom } from "store/uiStore";

export const useWebview = () => {
    const setInset = useSetAtom(insetAtom);
    const setDevice = useSetAtom(deviceAtom);

    if (window.ReactNativeWebView) {
        console.log = function (...props) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOGS', data: props }));
        };
        console.error = function (...props) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'ERROR_LOGS', data: props }));
        };
    }

    const onMessage = (e: MessageEvent) => {
        if (!isNativeApp()) return;
        if (e.origin && e.origin !== window.location.origin) return;

        const { type, data } = msgData(e);
        switch (type) {
            case APP_TO_WEB.INSET_INFO:
                setInset({
                    statusBarHeight: data.status_bar_height,
                    navigationBarHeight: data.navigation_bar_height
                })
                break;
            case APP_TO_WEB.DEVICE_INFO:
                setDevice({
                    model: data.model || '',
                    os: data.os || '',
                    isDarkMode: data.isDarkMode,
                    language: data.language || 'ko',
                    deviceId: data.deviceId || '',
                    countryCd: data.countryCd || '',
                    inset: data.inset || { statusBarHeight: 0, navigationBarHeight: 0 },
                });
                break;
            case APP_TO_WEB.SHARED_PLACE:
                if (data) {
                    const params = new URLSearchParams();
                    if (data.name) params.set('name', data.name);
                    if (data.address) params.set('address', data.address);
                    if (data.lat) params.set('lat', String(data.lat));
                    if (data.lng) params.set('lng', String(data.lng));
                    window.dispatchEvent(new CustomEvent('app-navigate', {
                        detail: { path: `/meeting/shared-place?${params.toString()}` }
                    }));
                }
                break;
            case 'SEND_TOKEN': {
                // 네이티브 → 웹뷰 토큰 푸시. 페이지 로드마다 native 가 sendToWeb('SEND_TOKEN')
                // 보내는데 수신 핸들러가 없으면 ProtectedRoute 가 localStorage 비어있다 판단해
                // /login 으로 튕기고 회귀(첫 부팅 흰 화면) 발생. 받는 즉시 localStorage 반영.
                //
                // 단, 방금 로그아웃했다면 받지 않습니다.
                // 로그아웃은 네이티브에 CLEAR_AUTH 를 보내고 기다리지 않고 바로 /login 으로
                // 다시 로드하는데, 그 페이지 로드가 끝나면 네이티브가 아직 안 지워진 MMKV 토큰을
                // SEND_TOKEN 으로 밀어붅니다. 여기서 그걸 받아 쓰면 로그아웃이 무효가 되고
                // 홈으로 돌아갑니다. 표시는 진짜 로그인(setTokens)에서 풀립니다.
                if (isJustLoggedOut()) break;
                try {
                    const at = data?.accessToken || data?.access_token;
                    const rt = data?.refreshToken || data?.refresh_token;
                    if (at) localStorage.setItem('accessToken', at);
                    if (rt) localStorage.setItem('refreshToken', rt);
                    if (data?.userId) sessionStorage.setItem('userId', String(data.userId));
                } catch { /* storage 실패 무시 */ }
                break;
            }
            case APP_TO_WEB.PLACE_SEARCH_RESULT:
            case APP_TO_WEB.PLACE_EXPLORE_RESULT:
            case APP_TO_WEB.GOOGLE_PLACE_SEARCH_RESULT:
            case APP_TO_WEB.CURRENT_LOCATION_RESULT:
            case APP_TO_WEB.TOKEN_REFRESH_RESULT:
            case APP_TO_WEB.PICK_IMAGE_RESULT:
            case APP_TO_WEB.SAVE_IMAGE_RESULT:
            case APP_TO_WEB.SHARE_IMAGE_RESULT:
                // 디버그: 네이티브에서 수신한 메시지 로그
                console.log('[useWebview]', type, 'received — requestId:', data?.requestId,
                    'hasImage:', !!data?.image,
                    'hasUri:', !!data?.image?.uri,
                    'hasBase64:', !!data?.image?.base64,
                    'cancelled:', data?.cancelled,
                    'error:', data?.error);
                if (data?.requestId) {
                    resolveNativeCallback(data.requestId, data);
                } else {
                    console.warn('[useWebview]', type, 'received but no requestId');
                }
                break;
            case APP_TO_WEB.NAVIGATE:
                if (data?.path) {
                    // 절대 URL(https://...) 방어 — pathname + search + hash 만 추출
                    let safePath: string = String(data.path);
                    try {
                        if (/^https?:\/\//i.test(safePath)) {
                            const u = new URL(safePath);
                            safePath = `${u.pathname}${u.search}${u.hash}`;
                        }
                    } catch {
                        // URL 파싱 실패 — 원본 사용
                    }

                    // ─── 방어 가드 1: 이미 같은 경로에 있으면 ACK 만 보내고 중복 점프 차단 ───
                    const currentFullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                    const currentPathOnly = window.location.pathname;
                    const targetPathOnly = safePath.split('?')[0].split('#')[0];
                    if (currentFullPath === safePath || currentPathOnly === targetPathOnly) {
                        // 이미 같은 화면 — 네이티브에 ACK 만 회신해 큐 비움 (재시도 방지)
                        requestApp(WEB_TO_APP.NAVIGATE_ACK, { path: safePath });
                        break;
                    }

                    // ─── 방어 가드 2: /meeting/create/* 진행 중일 때 /safe-return/* 점프는 큐잉 ───
                    // 사용자가 모임 생성 흐름 중인데 푸시 등으로 safe-return 으로 강제 이동되면 작업이 날아감.
                    // sessionStorage 에 보관해 두고, 모임 생성 흐름을 빠져나가면 그때 처리하도록 자체 라우터에서 flush.
                    const inMeetingCreate = currentPathOnly.startsWith('/meeting/create');
                    const isSafeReturn = targetPathOnly.startsWith('/safe-return/');
                    if (inMeetingCreate && isSafeReturn) {
                        try {
                            sessionStorage.setItem('deferred_navigate', safePath);
                        } catch {
                            // storage 실패 무시
                        }
                        // ACK 는 보냄 — 네이티브 큐는 비우되, 실제 이동은 deferred_navigate flush 시
                        requestApp(WEB_TO_APP.NAVIGATE_ACK, { path: safePath });
                        break;
                    }

                    // 라우터 마운트 전 도착했을 때를 대비해 sessionStorage 에 보관 (Router 가 mount 시 flush)
                    try {
                        sessionStorage.setItem('pending_navigate', safePath);
                    } catch {
                        // storage 실패 무시
                    }
                    // SPA 라우팅을 위한 커스텀 이벤트 발행 (이미 마운트된 경우 즉시 처리)
                    window.dispatchEvent(new CustomEvent('app-navigate', { detail: { path: safePath } }));
                    // 네이티브에 수신 확인 회신 — race 해결 보조
                    requestApp(WEB_TO_APP.NAVIGATE_ACK, { path: safePath });
                }
                break;
            case 'LOCATION_UPDATE':
                // 백그라운드에서 정밀 위치 업데이트 수신 → atom 갱신
                if (data?.lat && data?.lng) {
                    window.dispatchEvent(new CustomEvent('location-update', { detail: { lat: data.lat, lng: data.lng } }));
                }
                break;
            default:
                break;
        }
    }

    useEffect(() => {
        // injectedJS로 주입된 __DEVICE__ 정보를 atom에 저장
        if (window.__DEVICE__) {
            const d = window.__DEVICE__;
            setDevice({
                model: d.model || '',
                os: d.os || '',
                isDarkMode: d.isDarkMode,
                language: d.language || 'ko',
                deviceId: d.deviceId || '',
                countryCd: d.countryCd || '',
                inset: d.inset || { statusBarHeight: 0, navigationBarHeight: 0 },
            });
        }

        /* 네이티브 저장소가 비었는데 웹뷰만 토큰을 들고 있으면 한 번 밀어 줍니다.
           동기화가 한 방향이었습니다 — 네이티브는 페이지 로드마다 자기 토큰을
           웹으로 보내지만, 웹은 로그인·갱신 때만 네이티브로 보냅니다. 그래서
           네이티브가 한 번 비면 웹 accessToken 이 살아 있는 동안 갱신이 일어나지
           않아 계속 비어 있고, 네이티브가 직접 부르는 위치 전송이 토큰 없이
           조용히 실패합니다(노트10 실기기에서 그 상태를 확인).

           __APP__ 은 네이티브가 페이지 로드 전에 심어 둔 자기 저장소 값이라
           "지금 네이티브가 무엇을 들고 있는지" 의 근거로 쓸 수 있습니다.
           방금 로그아웃했다면 보내지 않습니다 — 로그아웃이 무효가 됩니다. */
        if (isNativeApp() && !isJustLoggedOut()) {
            try {
                const nativeAccess = (window as any).__APP__?.accessToken;
                const access = localStorage.getItem('accessToken');
                const refresh = localStorage.getItem('refreshToken');
                /* 만료된 토큰은 밀지 않습니다. 밀면 웹이 직접 갱신해 토큰을 회전시킨
                   직후에 옛 refreshToken 이 네이티브에 저장될 수 있고, 네이티브는
                   다음 갱신에서 4xx 를 받아 강제 로그아웃합니다. 만료 상태라면
                   갱신 흐름이 새 토큰을 네이티브까지 내려 주므로 손댈 필요가 없습니다. */
                if (!nativeAccess && access && !isTokenExpired(access)) {
                    /* userId 도 같이 보냅니다. 네이티브는 accessToken 과 userId 가
                       둘 다 있어야 로그인된 것으로 봅니다(hasAutoSignIn). */
                    let userId: string | undefined;
                    try { userId = decodeJwtPayload(access)?.sub || undefined; } catch { /* 형식 문제면 생략 */ }
                    requestApp(WEB_TO_APP.SEND_TOKEN, {
                        accessToken: access,
                        refreshToken: refresh || undefined,
                        access_token: access,
                        refresh_token: refresh || undefined,
                        userId,
                    });
                    console.log('[useWebview] 네이티브 저장소가 비어 있어 토큰을 밀어줍니다');
                }
            } catch { /* storage 접근 실패는 무시 — 기능이 아니라 보정입니다 */ }
        }

        window.addEventListener('message', onMessage);
        (document as any).addEventListener('message', onMessage);
        requestApp(WEB_TO_APP.MSG_READY);

        return () => {
            window.removeEventListener('message', onMessage);
            (document as any).removeEventListener('message', onMessage);
        }
    }, [])
}
